import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';

import { Category } from '~app/modules/generic/models';
import { CategoryFilter, ListTruncation } from '../../models';
import { environment } from '~env/environment';
import { CategoryImagesUtil } from '~app/modules/catalog/utils/category-images.util';
import {
  IMGIX_CATEGORY_FILTER_SEARCH_RESULT_PAGE_IMAGE_HEIGHT,
  IMGIX_CATEGORY_FILTER_SEARCH_RESULT_PAGE_IMAGE_WIDTH,
} from '~app/modules/catalog/constants';
import { CategorySelectedApplicationEvent } from '~shared/modules/application-event/models';
import { ApplicationCategorySelectedEventType } from '~shared/modules/application-event/constants';
import { ApplicationEventService } from '~shared/modules/application-event/services/application-event.service';
import { FormBuilder, FormGroup } from '@angular/forms';
import { FeatureToggleEnum } from '~shared/constants';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { Observable } from 'rxjs/internal/Observable';

@Component({
  selector: 'app-search-categories-filters-list',
  templateUrl: './categories-filters-list.component.html',
  styleUrls: ['./categories-filters-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CategoriesFiltersListComponent implements OnChanges {
  @Input() isNewDesign: boolean;
  @Input() categories: Category[];
  @Input() queryParams: { [key: string]: any };
  @Input() selectedCategoriesSlugs: string[];
  @Input() isDesktop: boolean;
  @Input() isMobileOrTablet: boolean;
  @Output() selectedCategoriesChanged = new EventEmitter<CategoryFilter[]>();

  isExpanded = false;
  hasTruncation = false;
  limitItems = ListTruncation.maxCategoriesCount;
  defaultCategoryImage = `${environment.cdnBaseUrl()}/images/product-catalog-placeholder.png`;

  fields;
  _categories: Category[];
  categoriesForm: FormGroup;

  constructor(private fb: FormBuilder, private applicationEventService: ApplicationEventService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedCategoriesSlugs || changes.categories) {
      this.initializeFilters();
    }
  }

  private initializeFilters() {
    this.hasTruncation = ListTruncation.maxCategoriesCount < this.categories.length;
    const rawFields = this.categories.reduce((acc, m) => ({ ...acc, [m.slug]: this.getFilterValue(m.slug) }), {});
    this.fields = this.reorderFields(rawFields);
    this._categories = this.getCategoryListToDisplay();
    this.categoriesForm = this.fb.group({ ...this.fields });
  }

  onCategoryChange(category): void {
    const mFilters: CategoryFilter[] = Object.entries(this.categoriesForm.value)
      .map((f) => ({ [f[0]]: f[1] }))
      .filter((f) => Object.values(f)[0])
      .reduce((acc: any, mf: any, idx) => {
        const data = this.categories.find((element) => element.slug === Object.keys(mf)[0]);
        return [...acc, { slug: data.slug, id: data.id }];
      }, []);
    this.firePLPCategoryTreeClickEvent(category);
    this.selectedCategoriesChanged.emit(mFilters);
  }

  getSource(categoryId): string {
    return CategoryImagesUtil.getCategoryImageSrc(
      categoryId,
      IMGIX_CATEGORY_FILTER_SEARCH_RESULT_PAGE_IMAGE_HEIGHT,
      IMGIX_CATEGORY_FILTER_SEARCH_RESULT_PAGE_IMAGE_WIDTH
    );
  }

  getSourceSet(categoryId): string {
    return CategoryImagesUtil.getCategoryImageSrcSet(
      categoryId,
      IMGIX_CATEGORY_FILTER_SEARCH_RESULT_PAGE_IMAGE_HEIGHT,
      IMGIX_CATEGORY_FILTER_SEARCH_RESULT_PAGE_IMAGE_WIDTH
    );
  }

  private getFilterValue(slug: string): boolean {
    const { filter: { categories = [] } = {} } = this.queryParams || {};

    return categories.some((m: string) => m === slug);
  }

  private reorderFields(fields: { [key: string]: any }): { [key: string]: boolean } {
    return Object.keys(fields)
      .sort((a, b) => {
        if (fields[a] > fields[b]) {
          return -1;
        }
        if (fields[a] < fields[b]) {
          return 1;
        }
        return 0;
      })
      .reduce((acc, key) => ({ ...acc, [key]: [fields[key]] }), {});
  }

  private getCategoryListToDisplay() {
    return Object.keys(this.fields).map((key) => this.categories.find((m) => m.slug === key));
  }

  private toggleShowList(currentListLength): void {
    this.limitItems =
      currentListLength === ListTruncation.maxCategoriesCount
        ? this.categories.length
        : ListTruncation.maxCategoriesCount;
    this.isExpanded = currentListLength < this.limitItems;
  }

  private firePLPCategoryTreeClickEvent(category: Category) {
    this.applicationEventService.publish(
      new CategorySelectedApplicationEvent(ApplicationCategorySelectedEventType.CategoryFilterPLP, category.name)
    );
  }
}
